import React, { useState } from 'react';
import { useOffline } from '../../components/App/AppOfflineContext';
import { useRouterContext } from '../../components/App/AppRouter';
import BusinessFunctionBadge from '../../components/BusinessFunctionBadge';
import type { ContinuumFormSummaryModel } from '../../defs/defs';
import { ContinuumForm } from '../../pages/PageContinuumFormDetail';
import { EqxLink } from '@equinoxx/react';
import EqxListGroup, { EqxListGroupItem } from './EqxListGroup/EqxListGroup';
import EqxModal, { useModalState } from './EqxModal/EqxModal';

const continuumFormPath = (formId: string | number, taskID?: string | number) => {
    const base = `/forms/${formId}`;
    if (taskID === undefined || taskID === null || taskID === '') return base;
    return `${base}?taskId=${encodeURIComponent(String(taskID))}`;
};

interface ContinuumFormListProps {
    forms: ContinuumFormSummaryModel[];
    isChild?: boolean;
    taskID?: string | number;
    useModal?: boolean;
    /** When not using a modal, open the form in a new browser tab instead of in-app navigation. */
    openInNewTab?: boolean;
}

const ContinuumFormList: React.FC<ContinuumFormListProps> = ({ forms, isChild, taskID, useModal = false, openInNewTab = false }) => {
    const router = useRouterContext();
    const { isOffline, isFileCached } = useOffline();
    const [selectedFormId, setSelectedFormId] = useState<string | number | null>(null);
    const modalState = useModalState();

    const handleFormClick = (formId: string | number) => {
        if (useModal) {
            setSelectedFormId(formId);
            modalState.show();
        } else {
            router.navigate(continuumFormPath(formId, taskID));
        }
    };

    const handleModalClose = () => {
        modalState.hide();
        setSelectedFormId(null);
    };

    return (
        <>
            <EqxListGroup isChild={isChild}>
                {forms.map((form) => {
                    const offlineClass = isOffline && !isFileCached(`/continuum-forms/${form.id}`) ? 'unavailable' : '';
                    const formPath = continuumFormPath(form.id, taskID);
                    return (
                    <EqxListGroupItem key={form.id}>
                        {!useModal && openInNewTab ? (
                            <EqxLink
                                to={formPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`clickable d-flex text-decoration-none text-body ${offlineClass}`.trim()}
                            >
                                <div className="m-1 me-3">{form.businessFunction && <BusinessFunctionBadge businessFunction={form.businessFunction} />}</div>
                                <div className="flex-grow-1">
                                    <h6 className="mb-0">{form.title}</h6>
                                    <div className="small text-muted">{form.reference}</div>
                                </div>
                            </EqxLink>
                        ) : (
                        <div className="clickable d-flex" onClick={() => handleFormClick(form.id)}>
                            <div className="m-1 me-3">{form.businessFunction && <BusinessFunctionBadge businessFunction={form.businessFunction} />}</div>
                            <div className="flex-grow-1">
                                <h6 className="mb-0">
                                    {useModal ? (
                                        <span className={offlineClass}>{form.title}</span>
                                    ) : (
                                        <EqxLink to={formPath} className={offlineClass}>
                                            {form.title}
                                        </EqxLink>
                                    )}
                                </h6>
                                <div className="small text-muted">{form.reference}</div>
                            </div>
                        </div>
                        )}
                    </EqxListGroupItem>
                    );
                })}
            </EqxListGroup>

            {useModal && selectedFormId && (
                <EqxModal
                    state={modalState}
                    title="Continuum Form"
                    onHide={handleModalClose}
                    noPadding={true}
                >
                    <ContinuumForm
                        formId={selectedFormId}
                        taskID={taskID}
                        onComplete={handleModalClose}
                    />
                </EqxModal>
            )}
        </>
    );
};

export default ContinuumFormList; 